import { useState } from "react";
import { Button, Typography, DenovoIcon, Pressable } from "@repo/design-system";
import { type IVirus, VIRUSES } from "../viruses";

export function BlueprintViruses() {
  const [selectedVirus, setSelectedVirus] = useState<IVirus | null>(null);

  if (selectedVirus) {
    return (
      <div className="flex flex-col gap-6">
        {/* Back button — between tab header and detail content */}
        <Button
          variant="transparent"
          leftIcon="arrow-left"
          onPress={() => setSelectedVirus(null)}
          className="w-fit text-contentBrand hover:text-contentBrand">
          Back to Viruses
        </Button>

        {/* Virus detail */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Typography
              as="h2"
              variant="heading-3"
              className="text-contentPrimary">
              {selectedVirus.name}
            </Typography>
            <Pressable
              onPress={() => {
                sessionStorage.setItem(
                  "bsad_virus_filter",
                  JSON.stringify([selectedVirus.abbreviation]),
                );
                window.open("/data", "_blank");
              }}
              className="w-fit cursor-pointer rounded-md border border-borderBrand bg-backgroundBrand/10 px-2.5 py-0.5 transition-colors hover:bg-backgroundBrand/20">
              <Typography
                variant="paragraph-m-semibold"
                className="text-contentBrand">
                {selectedVirus.abbreviation}
              </Typography>
            </Pressable>
          </div>

          {/* About the Virus */}
          <div className="flex flex-col gap-2">
            <Typography
              variant="paragraph-m-semibold"
              className="text-contentSecondary">
              About the Virus
            </Typography>
            <Typography
              variant="paragraph-l-regular"
              className="text-contentSecondary leading-relaxed">
              {selectedVirus.description.about}
            </Typography>
          </div>

          {/* Epidemiology */}
          <div className="flex flex-col gap-2">
            <Typography
              variant="paragraph-m-semibold"
              className="text-contentSecondary">
              Epidemiology
            </Typography>
            <Typography
              variant="paragraph-l-regular"
              className="text-contentSecondary leading-relaxed">
              {selectedVirus.description.epidemiology}
            </Typography>
          </div>

          {/* Pharmacological Targets */}
          <div className="flex flex-col gap-2">
            <Typography
              variant="paragraph-m-semibold"
              className="text-contentSecondary">
              Pharmacological Targets
            </Typography>
            <div className="flex flex-col gap-1.5">
              {selectedVirus.description.targets.map((target, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Typography
                    variant="paragraph-l-regular"
                    className="mt-0.5 flex-shrink-0 text-contentBrand">
                    •
                  </Typography>
                  <Typography
                    variant="paragraph-l-regular"
                    className="text-contentSecondary leading-relaxed">
                    {target}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Typography as="h2" variant="heading-3" className="text-contentPrimary">
        Viral Targets
      </Typography>

      <Typography
        variant="paragraph-l-regular"
        className="text-contentSecondary">
        Select a virus from the panel below to view detailed information.
      </Typography>

      <div className="flex flex-col gap-2">
        {VIRUSES.map((virus) => (
          <Pressable
            key={virus.id}
            onPress={() => setSelectedVirus(virus)}
            className="flex w-full cursor-pointer items-center justify-between rounded-xl border border-borderPrimary bg-backgroundPrimary px-5 py-4 text-left transition-all hover:border-borderBrand hover:bg-backgroundSecondary hover:shadow-light">
            <div className="flex flex-col gap-0.5">
              <Typography
                variant="paragraph-l-semibold"
                className="text-contentPrimary">
                {virus.name}
              </Typography>
              <Typography
                variant="paragraph-m-regular"
                className="text-contentSecondary">
                {virus.abbreviation}
              </Typography>
            </div>
            <DenovoIcon
              icon="chevron"
              width={16}
              height={16}
              className="flex-shrink-0 rotate-[-90deg] text-contentSecondary"
            />
          </Pressable>
        ))}
      </div>
    </div>
  );
}
